/**
 * Identidade visual, rota e SEO de cada uma das 6 Certificações da FEO.
 *
 * As cores são as oficiais de cada sub-marca (as mesmas do logo/hexágonos/CTA
 * nas LPs atuais). O conteúdo/copy vem de `data.ts`.
 */

export const CERT_SLUGS = ["lombar", "cervical", "eletro", "ombro", "tornozelo", "joelho"] as const;
export type CertSlug = (typeof CERT_SLUGS)[number];
/**
 * Slugs com LP interna. "quadril" é pré-lançamento (captura de lead): fica
 * fora de CERT_SLUGS/CERT_META para não entrar no catálogo, ofertas, copys
 * e passaporte — a meta dele é exportada separada (CERT_QUADRIL).
 */
export type CertLpSlug = CertSlug | "quadril";

export type CertMeta = {
  slug: CertLpSlug;
  /** Nome comercial completo. */
  nome: string;
  /** Área curta (usada em títulos e cards). */
  area: string;
  /** Rota da LP no Lovable. */
  path: string;
  /** Cor-tema principal (usada em CTA, números dos módulos, destaques). */
  from: string;
  /** Segunda cor do gradient (igual à primeira nas certificações mono). */
  to: string;
  seoTitle: string;
  seoDescription: string;
};

export const CERT_META: Record<CertLpSlug, CertMeta> = {
  lombar: {
    slug: "lombar",
    nome: "Certificação em Coluna Lombar",
    area: "Coluna Lombar",
    path: "/certificacao-lombar",
    from: "#F5A623",
    to: "#FFB93B",
    seoTitle: "Certificação em Coluna Lombar | FEO",
    seoDescription:
      "Curso 100% online com 60h sobre avaliação e tratamento da Coluna Lombar: 5 módulos, casos clínicos reais, aulas práticas e certificado ao final.",
  },
  cervical: {
    slug: "cervical",
    nome: "Certificação em Coluna Cervical",
    area: "Coluna Cervical",
    path: "/certificacao-cervical",
    from: "#FF4C6E",
    to: "#FF7A45",
    seoTitle: "Certificação em Coluna Cervical | FEO",
    seoDescription:
      "Curso 100% online com 60h sobre avaliação e tratamento da Coluna Cervical: 5 módulos, casos clínicos reais, aulas práticas e certificado ao final.",
  },
  eletro: {
    slug: "eletro",
    nome: "Certificação em Agentes Eletrofísicos",
    area: "Agentes Eletrofísicos",
    path: "/certificacao-eletro",
    from: "#8FE3D8",
    to: "#F2B3D0",
    seoTitle: "Certificação em Agentes Eletrofísicos | FEO",
    seoDescription:
      "Curso 100% online com 60h sobre Agentes Eletrofísicos: parâmetros, dosimetria, aplicações clínicas, casos clínicos reais e certificado ao final.",
  },
  ombro: {
    slug: "ombro",
    nome: "Certificação em Ombro",
    area: "Complexo do Ombro",
    path: "/certificacao-ombro",
    from: "#4FD9FF",
    to: "#2AA7FF",
    seoTitle: "Certificação em Ombro | FEO",
    seoDescription:
      "Curso 100% online com 60h sobre avaliação e tratamento do Complexo do Ombro: 5 módulos, casos clínicos reais, aulas práticas e certificado ao final.",
  },
  tornozelo: {
    slug: "tornozelo",
    nome: "Certificação em Tornozelo e Pé",
    area: "Tornozelo e Pé",
    path: "/certificacao-tornozelo",
    from: "#8A5CFF",
    to: "#C471FF",
    seoTitle: "Certificação em Tornozelo e Pé | FEO",
    seoDescription:
      "Curso 100% online com 60h sobre avaliação e tratamento do Tornozelo e Pé: 6 módulos, casos clínicos reais, aulas práticas e certificado ao final.",
  },
  joelho: {
    slug: "joelho",
    nome: "Certificação em Joelho",
    area: "Joelho",
    path: "/certificacao-joelho",
    from: "#B7FF3B",
    to: "#35D389",
    seoTitle: "Certificação em Joelho | FEO",
    seoDescription:
      "Curso 100% online com 60h sobre avaliação e tratamento do Joelho: 5 módulos, casos clínicos reais, aulas práticas e certificado ao final.",
  },
  // Lançamento pelo Lovable (venda direta; backup do grupo em /certificacao-quadril-grupo).
  // Página indexada; não entra em CERT_SLUGS/CERT_LIST (catálogo/ofertas).
  quadril: {
    slug: "quadril",
    nome: "Certificação em Quadril",
    area: "Quadril",
    path: "/certificacao-quadril",
    from: "#ff3366",
    to: "#3300cc",
    seoTitle: "Certificação em Quadril · curso online de 60h | FEO",
    seoDescription:
      "Certificação em Quadril da FEO: curso 100% online com 60h, 6 módulos, casos clínicos reais e aulas práticas de avaliação e reabilitação do quadril. De R$697 por R$397 com cupom PROMO.",
  },
};

/** As 6 certificações do catálogo (slug tipado como CertSlug). */
export const CERT_LIST = CERT_SLUGS.map((s) => CERT_META[s] as CertMeta & { slug: CertSlug });

/** Grupo de WhatsApp de pré-matrícula da Certificação Quadril. */
export const QUADRIL_GRUPO_WPP = "https://chat.whatsapp.com/Fch5urM5wGR6dbGu7TJTKU";

/**
 * As 6 LPs de venda do Lovable entram no Google só na virada do Wix.
 * Trocar para true junto com os links do site/catálogo/anúncios.
 */
export const CERT_LP_INDEXAVEL = true;

export const CERT_WHATSAPP = "https://api.whatsapp.com/send?1=pt_BR&phone=5511930316779";
